import type { StorylineChartEvent } from '@/mocks/marketStorylinesMock';

type Props = {
  symbol: string;
  event: StorylineChartEvent;
  onClose: () => void;
  className?: string;
};

export function StorylineEventDetail({ symbol, event, onClose, className = '' }: Props) {
  const accentBorder =
    event.sentiment === 'positive'
      ? 'border-emerald-100'
      : event.sentiment === 'negative'
        ? 'border-rose-100'
        : 'border-indigo-100';
  const accentBg =
    event.sentiment === 'positive'
      ? 'bg-emerald-50/60'
      : event.sentiment === 'negative'
        ? 'bg-rose-50/60'
        : 'bg-indigo-50/60';

  return (
    <div className={`rounded-xl border p-5 ${accentBorder} ${accentBg} ${className}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{symbol}</p>
          <p className="text-base font-semibold text-gray-900">{event.label}</p>
          <p className="text-xs text-gray-500">
            {event.date}
            {event.percentChange != null
              ? ` · ${event.percentChange > 0 ? '+' : ''}${event.percentChange.toFixed(2)}%`
              : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-xs text-gray-500 hover:text-gray-800"
        >
          Close
        </button>
      </div>
      {event.primaryCatalyst ? (
        <p className="mb-2 text-sm font-medium text-gray-800">{event.primaryCatalyst}</p>
      ) : null}
      <p className="text-sm leading-relaxed text-gray-700">{event.summary}</p>
      {event.glp1Context ? (
        <p className="mt-3 text-xs leading-relaxed text-gray-600">
          <span className="font-medium text-gray-800">GLP-1 context: </span>
          {event.glp1Context}
        </p>
      ) : null}
      {event.citations?.length ? (
        <ul className="mt-4 space-y-1.5 border-t border-gray-200/60 pt-3">
          {event.citations.slice(0, 5).map((cite) => (
            <li key={cite.url}>
              <a
                href={cite.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-indigo-700 hover:underline"
              >
                {cite.title}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
